import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo } from "react";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    NonDetectStats,
} from "../../../../../apiClient/generated";
import {
    useDataProvidersApi,
    useNondetectsApiClient,
} from "../../../../../hooks";
import { useDrawAndFilterControlsV2 } from "../../../hooks/areaFilter";
import { useMapData, useMapFilters } from "../../../hooks/mapDataAndFilters";
import { useMap } from "../../../hooks/mapState";
import { InsightsContainer } from "../../../ui/MapControlsV2";
import { DataProviderPickerCheckBoxMenu } from "../../../ui/Menu";
import {
    type CountsCardCategory,
    CountsCardView,
    SettingsDropdownProvider,
} from "./ui";

// Display config for the detection limit bins returned by the API, in the
// order they should be plotted (most sensitive first).
const DETECTION_LIMIT_BINS = [
    {
        bin: "under_10",
        label: "< 10 kg/h",
        color: "#355F73",
    },
    {
        bin: "10_to_50",
        label: "10 to < 50 kg/h",
        color: "#6A9FB5",
    },
    {
        bin: "50_to_100",
        label: "50 to < 100 kg/h",
        color: "#B8D8E3",
    },
    {
        bin: "100_to_500",
        label: "100 to < 500 kg/h",
        color: "#E3B23C",
    },
    {
        bin: "over_500",
        label: "500 kg/h or more",
        color: "#BC6C25",
    },
] as const;

interface NonDetectsProviderPickerProps {
    selected:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
    setSelected: (
        value:
            | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
            | "all",
    ) => void;
}

const NonDetectsProviderPicker = (props: NonDetectsProviderPickerProps) => {
    const apiClient = useDataProvidersApi();

    const providersQuery = useQuery({
        queryKey: ["dataProviders", { hasData: true }],
        queryFn: async () => {
            const response = await apiClient.dataProvidersList({
                hasData: true,
            });
            return response.results;
        },
        staleTime: Infinity,
    });

    return (
        <DataProviderPickerCheckBoxMenu
            label="Providers"
            dataProviders={providersQuery.data}
            selected={props.selected}
            setSelected={props.setSelected}
        />
    );
};

export const NonDetectInsights = () => {
    const apiClient = useNondetectsApiClient();
    const { debounced } = useMap("mainMap");
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");
    const { selectedFilters, filterData, areaToFetch } =
        useDrawAndFilterControlsV2("mainMap");
    const { selectedContext } = useMapData("mainMap");

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore =
                filterState.emissions.endDateFilter !== ""
                    ? new Date(filterState.emissions.endDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.end) {
                dateBefore = new Date(selectedContext.dateRangeToShowData.end);
            }
            let dateAfter =
                filterState.emissions.startDateFilter !== ""
                    ? new Date(filterState.emissions.startDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.start) {
                dateAfter = new Date(selectedContext.dateRangeToShowData.start);
            }
            return { dateBefore, dateAfter };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            selectedContext.dateRangeToShowData,
        ]),
        400,
    );

    const providerFilter = filterState.nonDetects.filters.providers;

    const nonDetectsStats = useQuery({
        queryKey: [
            "nonDetectsStats",
            areaToFetch,
            providerFilter,
            debouncedDateRange,
            selectedFilters,
            filterData,
        ],
        queryFn: async (): Promise<NonDetectStats[]> => {
            return await apiClient.nondetectsSitenondetectNonDetectStatsList({
                providerWithSource:
                    providerFilter === "all"
                        ? undefined
                        : (JSON.stringify(providerFilter) as any),
                timestampAfter: debouncedDateRange.dateAfter,
                timestampBefore: debouncedDateRange.dateBefore,
                locationWithin: areaToFetch,
                locationPreset:
                    filterData && selectedFilters.length > 0
                        ? selectedFilters
                        : undefined,
            });
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled:
            !!debounced.areaOnScreen &&
            (providerFilter === "all" || providerFilter.length > 0),
        staleTime: 500,
    });

    // Bins the API didn't report are still shown, as zero.
    const categories = useMemo((): CountsCardCategory[] => {
        const countsByBin = new Map(
            (nonDetectsStats.data || []).map((d) => [d.bin, d.count]),
        );
        return DETECTION_LIMIT_BINS.map((bin) => ({
            key: bin.bin,
            label: bin.label,
            color: bin.color,
            count: countsByBin.get(bin.bin) ?? 0,
        }));
    }, [nonDetectsStats.data]);

    return (
        <InsightsContainer>
            <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Site Non-Detect Counts</p>
                <SettingsDropdownProvider>
                    <NonDetectsProviderPicker
                        selected={providerFilter}
                        setSelected={(v) =>
                            updateMapFilterState((draft) => {
                                draft.nonDetects.filters.providers = v;
                            })
                        }
                    />
                </SettingsDropdownProvider>
            </div>
            {providerFilter !== "all" && providerFilter.length === 0 ? (
                <div className="text-xs text-ae-slate-600 py-4 text-center">
                    Select at least one provider to see non-detect coverage.
                </div>
            ) : (
                <CountsCardView
                    data={categories}
                    loading={nonDetectsStats.isFetching}
                    totalLabel="non-detects in view"
                />
            )}
        </InsightsContainer>
    );
};
